import type Application from "../../types/application";
import { Body, SecondaryBody } from "@sprout/typography";
import PreviewIcon from "@sprout/icons/preview";
import EllipsisIcon from "@sprout/icons/ellipsis";
import { Row, Stack } from "@sprout/layout";
import useTranslation from "@hooks/use_translation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu.tsx";
import DreamJobModal from "./dream_job_modal";
import { daysUntilNextMonth } from "@utils/date_utils";
import { useEffect, useState } from "react";
import { MarkInactiveModal } from "@components/applications/mark_inactive_modal.tsx";
import { IconButton } from "@sprout/icon_button.tsx";

type Props = {
  application: Application;
  hasTokens: boolean;
  onAddDreamJob: () => void;
};

const ApplicationEllipsis = ({ application, hasTokens, onAddDreamJob }: Props) => {
  const { t } = useTranslation("applications");

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDreamJobModalOpen, setIsDreamJobModalOpen] = useState(false);
  const [isMarkInactiveModalOpen, setIsMarkInactiveModalOpen] = useState(false);

  const dreamJobDescription = () => {
    if (application.dream_job) {
      return t("dream_job.already_set");
    } else if (application.duplicate) {
      return t("dream_job.duplicate");
    } else if (application.inactive) {
      return t("dream_job.inactive");
    } else if (!hasTokens) {
      return t("dream_job.no_tokens", { count: daysUntilNextMonth() });
    } else {
      return t("dream_job.description");
    }
  };

  useEffect(() => {
    if (!isDreamJobModalOpen && !isMarkInactiveModalOpen) {
      return;
    }

    setIsDropdownOpen(false);
  }, [isDreamJobModalOpen, isMarkInactiveModalOpen]);

  return (
    <>
      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <IconButton label="Open menu" icon={EllipsisIcon} size="md" />
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-74" align="end">
          <DropdownMenuItem
            onSelect={() => {
              window.open(application.job_post_url, "_blank");
            }}
          >
            <Row align="center" gap={"12px"}>
              <Body>{t("dream_job.open_job_post")}</Body>
              <PreviewIcon />
            </Row>
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={
              !hasTokens || application.dream_job || application.inactive || application.duplicate
            }
            onSelect={() => setIsDreamJobModalOpen(true)}
          >
            <Row gap={"8px"}>
              <Stack>
                <Body>{t("dream_job.set")}</Body>
                <div className="dream-job-subtext">
                  <SecondaryBody>{dreamJobDescription()}</SecondaryBody>
                </div>
              </Stack>
            </Row>
          </DropdownMenuItem>
          {!application.inactive && (
            <DropdownMenuItem onSelect={() => setIsMarkInactiveModalOpen(true)}>
              <Body>{t("mark_as_inactive")}</Body>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <DreamJobModal
        application={application}
        afterSuccess={onAddDreamJob}
        open={isDreamJobModalOpen}
        onOpenChange={setIsDreamJobModalOpen}
      />
      <MarkInactiveModal
        application={application}
        open={isMarkInactiveModalOpen}
        onOpenChange={setIsMarkInactiveModalOpen}
      />
    </>
  );
};

export default ApplicationEllipsis;
